import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { cn } from '@/lib/utils';

/**
 * Chat markdown renderer for the Writer chat.
 *
 * Mirrors the stack used by ChatGPT / Claude / Cursor: react-markdown as
 * the renderer, remark-gfm for tables/lists/strikethrough, remark-math +
 * rehype-katex for `$…$` and `$$…$$` (important for a LaTeX writing
 * assistant), and rehype-sanitize as a hard XSS boundary — never trust
 * model output.
 *
 * Streaming-safe: partial `**bold` or unterminated fences are gracefully
 * rendered as text by react-markdown, so mid-stream tokens don't throw.
 */

// Extend the default sanitize schema so KaTeX (which emits <span
// class="katex …">) and our link overrides survive sanitization. Default
// schema strips `className` and most attrs from spans/code otherwise.
const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span ?? []), 'className', 'style'],
    div: [...(defaultSchema.attributes?.div ?? []), 'className', 'style'],
    code: [...(defaultSchema.attributes?.code ?? []), 'className'],
    pre: [...(defaultSchema.attributes?.pre ?? []), 'className'],
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      ['target'],
      ['rel'],
    ],
    // Allow KaTeX MathML output
    math: [...(defaultSchema.attributes?.math ?? []), 'xmlns'],
    annotation: [...(defaultSchema.attributes?.annotation ?? []), 'encoding'],
  },
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    // KaTeX MathML tags
    'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'ms', 'mtext',
    'msup', 'msub', 'msubsup', 'mfrac', 'msqrt', 'mroot', 'mspace',
    'mover', 'munder', 'munderover', 'mtable', 'mtr', 'mtd',
    'annotation', 'annotation-xml',
  ],
};

const components: Components = {
  a: ({ node: _n, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="underline underline-offset-2 hover:opacity-80"
    />
  ),
  code: ({ node: _n, className, children, ...props }) => {
    const isBlock = /language-/.test(className ?? '');
    if (isBlock) {
      return (
        <code className={cn('font-mono text-xs', className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-background/60 px-1 py-0.5 font-mono text-[0.85em]"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ node: _n, children, ...props }) => (
    <pre
      className="my-2 overflow-x-auto rounded-lg border border-border bg-background/80 p-3 text-xs"
      {...props}
    >
      {children}
    </pre>
  ),
  h1: ({ node: _n, ...props }) => (
    <h1 className="mt-3 mb-1 text-base font-semibold" {...props} />
  ),
  h2: ({ node: _n, ...props }) => (
    <h2 className="mt-3 mb-1 text-sm font-semibold" {...props} />
  ),
  h3: ({ node: _n, ...props }) => (
    <h3 className="mt-2 mb-1 text-sm font-semibold" {...props} />
  ),
  h4: ({ node: _n, ...props }) => (
    <h4 className="mt-2 mb-1 text-sm font-medium" {...props} />
  ),
  p: ({ node: _n, ...props }) => (
    <p className="my-1 whitespace-pre-wrap leading-relaxed" {...props} />
  ),
  ul: ({ node: _n, ...props }) => (
    <ul className="my-1 list-disc pl-5 space-y-0.5" {...props} />
  ),
  ol: ({ node: _n, ...props }) => (
    <ol className="my-1 list-decimal pl-5 space-y-0.5" {...props} />
  ),
  li: ({ node: _n, ...props }) => <li className="leading-relaxed" {...props} />,
  blockquote: ({ node: _n, ...props }) => (
    <blockquote
      className="my-2 border-l-2 border-border pl-3 italic opacity-90"
      {...props}
    />
  ),
  table: ({ node: _n, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs" {...props} />
    </div>
  ),
  th: ({ node: _n, ...props }) => (
    <th
      className="border border-border bg-background/60 px-2 py-1 text-left font-medium"
      {...props}
    />
  ),
  td: ({ node: _n, ...props }) => (
    <td className="border border-border px-2 py-1 align-top" {...props} />
  ),
  hr: ({ node: _n, ...props }) => (
    <hr className="my-3 border-border" {...props} />
  ),
};

interface ChatMarkdownProps {
  text: string;
  className?: string;
}

const ChatMarkdownImpl: React.FC<ChatMarkdownProps> = ({ text, className }) => {
  return (
    <div className={cn('space-y-1 break-words', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, [rehypeSanitize, schema]]}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

// Memoize on the text string — streaming appends tokens rapidly and
// re-parsing the AST every keystroke is the main cost of markdown chat.
export const ChatMarkdown = React.memo(
  ChatMarkdownImpl,
  (a, b) => a.text === b.text && a.className === b.className,
);
